import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';

import SuppliersCreateModal from '../suppliers/SuppliersCreateModal';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import SelectField from 'CommonComponents/SelectField';
import Tooltip from 'CommonComponents/Tooltip';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

/**
 * SponsorCommitmentContract
 *
 * Supplier + contract block of the single-screen Commit Sponsor form — the sponsorship sibling of
 * RevenuesCommitmentContract, sharing its markup so the three commit screens look alike.
 *
 * Sponsorship keeps one behaviour the others do not: once a sponsor already has a commitment, the
 * supplier is locked to that commitment's supplier and "Add new Supplier" is withheld
 * (isAddSupplierAllowed, resolved server-side). That matters more now that a sponsor can be
 * committed repeatedly — every commitment of one deal belongs to the same counterparty.
 */
export default function SponsorCommitmentContract({
    suppliers,
    isAddSupplierAllowed,
    errors,
    touched,
    values,
    setFieldValue,
    onSupplierCreated,
    suppliersIndexUrl,
    suppliersStoreUrl,
    suppliersCreateUrl,
    sponsorName,
}) {
    const [suppliersCreateModalElement, setSuppliersCreateModalElement] = useState(null);

    const suppliersOptions = useMemo(() => {
        return suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }));
    }, [suppliers]);

    const suppliersCreateModalRef = useRef();
    useEffect(() => {
        suppliersCreateModalRef && setSuppliersCreateModalElement(new Modal(suppliersCreateModalRef.current, modalOptions(false)));
    }, []);

    const closeModal = () => {
        $('#create-supplier-modal').modal('hide');
        suppliersCreateModalElement && suppliersCreateModalElement.hide();
    };

    const successHandler = () => {
        closeModal();
        onSupplierCreated();
        $('#create-supplier-modal').modal('hide');
    };

    const formatContractOptionLabel = (option, { context }) => {
        if (context === 'menu' && option.tooltip) {
            return (
                <Tooltip
                    allowTooltip
                    placement="right"
                    tooltipType="text"
                    offset={[0, 18]}
                    tooltipContent={
                        <div className="tw-text-xs tw-space-y-1">
                            <div>
                                <span className="tw-font-semibold">{$t('Supplier Name')}:</span> {option.tooltip.supplierName}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Contract Value')}:</span> {option.tooltip.contractValue}
                            </div>
                        </div>
                    }
                >
                    <div>{option.label}</div>
                </Tooltip>
            );
        }
        return option.label;
    };

    const contractsOptions = useMemo(() => {
        const supplier = suppliers.find((sup) => sup.id == values.supplier_id);

        let options = [{ value: '', label: $t('New Contract') }];

        options = options.concat(
            supplier?.supplier_contract.map((contract) => ({
                value: contract.id,
                label: contract.contract_reference,
                tooltip: {
                    supplierName: supplier.name,
                    contractValue: contract.cashflow?.total ?? 0,
                },
            })) || [],
        );

        return options;
    }, [values.supplier_id, suppliers]);

    return (
        <Fragment>
            <div className="tw-space-y-4">
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <div className="tw-grow tw-flex tw-flex-col tw-space-y-1.5 tw-min-w-0">
                        <label htmlFor="supplier_id" className="text-input__label">
                            {$t('Recommended Supplier')} <span className="tw-text-red tw-text-sm">*</span>
                        </label>
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                            options={suppliersOptions}
                            value={values.supplier_id}
                            backgroundColor="#e4e4e480"
                            placeholder={$t('Please select')}
                            onChange={(supplier) => {
                                setFieldValue('supplier_id', supplier?.value || '');
                                setFieldValue('contract_id', '');
                            }}
                        />
                        {touched.supplier_id && errors.supplier_id && <div className="tw-text-red tw-text-sm">{errors.supplier_id}</div>}
                    </div>

                    {isAddSupplierAllowed && (
                        <div className="tw-self-end tw-shrink-0">
                            <Button
                                variant="success"
                                className="!tw-h-9 tw-whitespace-nowrap"
                                onClick={() => suppliersCreateModalElement && suppliersCreateModalElement.show()}
                            >
                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                {$t('Add new Supplier')}
                            </Button>
                        </div>
                    )}
                </div>

                {values.supplier_id && (
                    <div className="tw-grow tw-flex tw-flex-col tw-space-y-1.5">
                        <label htmlFor="contract_id" className="text-input__label">
                            {$t('Contract Reference')}
                        </label>
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                            options={contractsOptions}
                            value={values.contract_id}
                            formatOptionLabel={formatContractOptionLabel}
                            backgroundColor="#e4e4e480"
                            placeholder={$t('Please select')}
                            onChange={(contract) => {
                                setFieldValue('contract_id', contract?.value || '');
                            }}
                        />
                        {touched.contract_id && errors.contract_id && <div className="tw-text-red tw-text-sm">{errors.contract_id}</div>}
                    </div>
                )}

                <div className="tw-grid tw-grid-cols-2 tw-gap-x-2">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Contract Start Date')}</label>
                        <DatePickerField
                            name="contract_started_at"
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            className={classNames(
                                'text-input !tw-bg-grey/50',
                                errors.contract_started_at && touched.contract_started_at ? 'tw-border tw-border-red' : 'tw-border-0',
                            )}
                            value={values.contract_started_at}
                            onChange={setFieldValue}
                            wrapped
                        />
                        {errors.contract_started_at && touched.contract_started_at && (
                            <div className="tw-text-red tw-text-sm">{errors.contract_started_at}</div>
                        )}
                    </div>
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Contract End Date')}</label>
                        <DatePickerField
                            name="contract_ended_at"
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            className={classNames(
                                'text-input !tw-bg-grey/50',
                                errors.contract_ended_at && touched.contract_ended_at ? 'tw-border tw-border-red' : 'tw-border-0',
                            )}
                            value={values.contract_ended_at}
                            onChange={setFieldValue}
                            wrapped
                        />
                        {errors.contract_ended_at && touched.contract_ended_at && <div className="tw-text-red tw-text-sm">{errors.contract_ended_at}</div>}
                    </div>
                </div>
            </div>

            <SuppliersCreateModal
                modalId={'create-supplier-modal'}
                modalRef={suppliersCreateModalRef}
                modalElement={suppliersCreateModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                suppliersIndexUrl={suppliersIndexUrl}
                suppliersStoreUrl={suppliersStoreUrl}
                suppliersCreateUrl={suppliersCreateUrl}
                supplierName={sponsorName}
            />
        </Fragment>
    );
}
